import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Sparkles, Compass, ChevronRight, Info, Clock, CheckCircle2, Lock, Circle,
  PlayCircle, FileText, Bot, PenSquare, Plus, Map, RotateCcw, UserCog, Award, TrendingUp,
  CalendarClock, Activity as ActivityIcon, Download, X, Rocket,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { UpgradeNudge } from "@/components/UpgradeIntelligence";
import { FirstWin } from "@/components/FirstWin";

function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8">
      {eyebrow && <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{eyebrow}</div>}
      <h1 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">{title}</h1>
      {subtitle && <p className="text-[#A1A1AA] mt-2 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

const Card = ({ children, className = "", testid }) => (
  <div className={`rounded-2xl border border-white/8 bg-[#121214] p-6 ${className}`} data-testid={testid}>{children}</div>
);
const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon className="w-4 h-4 text-[#D4AF37]" />
    <span className="text-[11px] uppercase tracking-wider text-[#71717A]">{children}</span>
  </div>
);

const JOURNEY_ICON = { completed: CheckCircle2, in_progress: PlayCircle, recommended: Compass, locked: Lock, not_started: Circle };
const JOURNEY_COLOR = {
  completed: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
  in_progress: "text-amber-300 border-amber-500/30 bg-amber-500/5",
  recommended: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  locked: "text-[#52525B] border-white/8",
  not_started: "text-[#71717A] border-white/8",
};

function OnboardingBanner({ data, onStart, onDismiss }) {
  if (data.onboarding.completed || data.onboarding.skipped || data.onboarding.answered_count > 3) return null;
  return (
    <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#151310] to-[#121214] p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="onboarding-banner">
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
        <div>
          <div className="text-white font-medium">Finish setting up your business profile</div>
          <div className="text-xs text-[#A1A1AA]">Answer a few quick questions so we can personalize your journey.</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onStart} data-testid="onboarding-banner-start"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 h-9 text-sm">Complete setup</button>
        <button onClick={onDismiss} data-testid="onboarding-banner-dismiss" className="text-[#71717A] hover:text-white p-2"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const QUICK = [
  { key: "ai", label: "Ask AI Business Coach", icon: Bot, path: "/ai-assistant" },
  { key: "workflows", label: "Guided AI Workflows", icon: Sparkles, path: "/workflows" },
  { key: "doc", label: "Create a Document", icon: FileText, path: "/documents" },
  { key: "task", label: "Add a Task", icon: Plus, path: "/progress" },
  { key: "journey", label: "View Business Journey", icon: Map, path: "/progress" },
  { key: "profile", label: "Update Business Profile", icon: UserCog, path: "/onboarding" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [saved, setSaved] = useState([]);

  useEffect(() => {
    // Claim any anonymous tool results tied to this account (idempotent, ownership-safe).
    api.post("/tools/claim").then(({ data }) => {
      if (data.count) setSaved(data.claimed);
    }).catch(() => {});
    api.get("/tools/saved-results").then(({ data }) => {
      if (data.results?.length) setSaved(data.results);
    }).catch(() => {});
    if (localStorage.getItem("fv_onboard")) { navigate("/onboarding"); return; }
    api.get("/dashboard/smart").then(({ data }) => setD(data)).catch(() => {});
    // Returning from a re-engagement email — record the truthful funnel signal.
    if (new URLSearchParams(window.location.search).get("reengage") === "1") {
      api.post("/analytics/track", { event: "reengagement_clicked", props: {} }).catch(() => {});
    }
  }, [navigate]);

  const openResult = async (r) => {
    try { await api.get(`/tools/saved-results/${r.result_id}`); } catch (e) { /* ignore */ }
    navigate(r.next_action?.path || "/documents");
  };

  const dismissOnboarding = async () => {
    try { await api.post("/onboarding/skip"); } catch (e) { /* ignore */ }
    setD((p) => ({ ...p, onboarding: { ...p.onboarding, skipped: true } }));
  };

  if (!d) return <div className="fv-fade-up"><PageHeader eyebrow="Command Center" title="Welcome back" subtitle="Loading your personalized dashboard…" /></div>;

  const g = d.greeting;
  const primary = d.recommendation.primary;
  const secondary = d.recommendation.secondary || [];
  const resumePath = QUICK[4].path;

  return (
    <div className="fv-fade-up" data-testid="smart-dashboard">
      <PageHeader eyebrow="Command Center"
        title={`Welcome back, ${g.first_name}`}
        subtitle={g.business_name ? `Let's continue building ${g.business_name}.` : "Let's continue building your business."} />

      <OnboardingBanner data={d} onStart={() => navigate("/onboarding")} onDismiss={dismissOnboarding} />

      {saved.length > 0 && (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-[#0f1a14] to-[#121214] p-5 mb-6" data-testid="saved-result-banner">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-medium">Your work was saved — continue where you left off</span>
          </div>
          <div className="space-y-2">
            {saved.map((r) => (
              <div key={r.result_id} className="flex items-center justify-between gap-3 rounded-xl bg-[#0A0A0B] border border-white/8 p-3 flex-wrap" data-testid={`saved-result-${r.tool}`}>
                <div className="text-sm text-[#D4D4D8]">{r.title}<span className="text-[#52525B]"> · saved from your free tool</span></div>
                <button onClick={() => openResult(r)} data-testid={`saved-result-open-${r.tool}`}
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 h-9 text-sm">
                  {r.next_action?.label || "Continue"} <ChevronRight className="w-4 h-4 inline ml-1" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <UpgradeNudge compact />
      <FirstWin />

      {/* Hero: completion / stage / health / recommended next action */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="grid grid-cols-3 gap-4 lg:col-span-1">
          <Card testid="stat-completion" className="!p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Complete</div>
            <div className="font-heading text-2xl text-white">{d.completion_pct}%</div>
          </Card>
          <Card testid="stat-stage" className="!p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Stage</div>
            <div className="text-sm text-white font-medium mt-1 capitalize leading-tight">{d.stage}</div>
          </Card>
          <Card testid="stat-health" className="!p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Health</div>
            <div className="font-heading text-2xl text-white">{d.health.score}</div>
          </Card>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6" data-testid="recommended-next">
          <div className="flex items-center gap-2 mb-3"><Compass className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">Recommended Next Step</span></div>
          <h3 className="font-heading text-2xl text-white mb-1.5">{primary.title}</h3>
          <p className="text-[#A1A1AA] text-sm mb-3">{primary.description}</p>
          <p className="text-[#D4AF37]/90 text-xs mb-4 flex items-start gap-1.5" data-testid="recommended-why">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span><b>Why this is recommended:</b> {primary.why}</span>
          </p>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => navigate(primary.action_path)} data-testid="recommended-start-btn"
              className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-6 h-11 flex items-center gap-2">
              {primary.action_label} <ArrowUpRight className="w-4 h-4" /></button>
            <button onClick={() => navigate(resumePath)} data-testid="resume-last-btn"
              className="rounded-full px-5 h-11 border border-white/15 text-[#D4D4D8] hover:border-[#D4AF37]/40 flex items-center gap-2 text-sm">
              <RotateCcw className="w-4 h-4" /> Resume Last Step</button>
            <button onClick={() => navigate("/progress")} data-testid="continue-journey-btn"
              className="rounded-full px-5 h-11 border border-white/15 text-[#D4D4D8] hover:border-[#D4AF37]/40 flex items-center gap-2 text-sm">
              <Map className="w-4 h-4" /> Continue Business Journey</button>
          </div>
        </div>
      </div>

      {/* Business Journey tracker */}
      <Card testid="business-journey" className="mb-6">
        <SectionTitle icon={Map}>Your business journey</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {d.journey.map((s, i) => {
            const Icon = JOURNEY_ICON[s.status] || Circle;
            return (
              <button key={s.key} data-testid={`journey-${s.key}`} onClick={() => navigate(s.route)}
                className={`text-left rounded-xl border p-3.5 transition-colors hover:border-[#D4AF37]/40 ${JOURNEY_COLOR[s.status]}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-[#71717A]">{String(i + 1).padStart(2, "0")}</span>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-xs text-white font-medium leading-tight">{s.label}</div>
                <div className="text-[10px] mt-1 capitalize opacity-80">{s.status === "locked" ? "View anyway" : s.status.replace("_", " ")}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Today's task + Continue where left off + Health categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card testid="today-task">
          <SectionTitle icon={Compass}>Today's recommended task</SectionTitle>
          <h3 className="text-white font-medium mb-1">{d.today_task.title}</h3>
          <p className="text-xs text-[#A1A1AA] mb-3">{d.today_task.description}</p>
          <div className="flex items-center gap-2 text-xs text-[#71717A] mb-4"><Clock className="w-3.5 h-3.5" /> ~{d.today_task.est_minutes} min</div>
          <button onClick={() => navigate(d.today_task.action_path)} data-testid="today-task-start"
            className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-10 text-sm">Start</button>
        </Card>

        <Card testid="continue-left-off">
          <SectionTitle icon={RotateCcw}>Continue where you left off</SectionTitle>
          {d.resume.last_activity ? (
            <>
              <div className="text-sm text-white mb-1">{d.resume.last_activity}</div>
              {d.resume.workflow_step && <div className="text-xs text-[#A1A1AA] mb-1">Formation step: {String(d.resume.workflow_step).replace(/_/g, " ")}</div>}
              <div className="text-[11px] text-[#52525B] mb-4">{d.resume.last_accessed ? new Date(d.resume.last_accessed).toLocaleString() : ""}</div>
              <button onClick={() => navigate(resumePath)} data-testid="continue-resume-btn"
                className="w-full rounded-full h-10 text-sm border border-white/15 text-[#D4D4D8] hover:border-[#D4AF37]/40">Resume</button>
            </>
          ) : <p className="text-sm text-[#52525B]">Start your first module and you can pick it back up here anytime.</p>}
        </Card>

        <Card testid="health-score">
          <SectionTitle icon={TrendingUp}>Business Health Score</SectionTitle>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-heading text-3xl text-white">{d.health.score}</span>
            <span className="text-xs text-[#71717A]">/ 100 · {d.health.band}</span>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {d.health.categories.map((c) => (
              <div key={c.key}>
                <div className="flex justify-between text-[11px] mb-0.5"><span className="text-[#A1A1AA]">{c.label}</span><span className="text-[#71717A]">{c.score}%</span></div>
                <div className="h-1.5 rounded-full bg-white/8"><div className="h-full fv-gold-gradient rounded-full" style={{ width: `${c.score}%` }} /></div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#52525B] mt-3">An educational readiness indicator — not a guarantee of legal compliance, funding/credit approval, or business success.</p>
        </Card>
      </div>

      {/* Secondary recommendations */}
      {secondary.length > 0 && (
        <Card testid="secondary-recs" className="mb-6">
          <SectionTitle icon={Sparkles}>Other recommended steps</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-3">
            {secondary.map((s) => (
              <button key={s.id} onClick={() => navigate(s.action_path)} data-testid={`secondary-${s.id}`}
                className="text-left rounded-xl border border-white/8 bg-[#0f0f11] hover:border-[#D4AF37]/30 p-4 transition-colors">
                <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{s.module}</div>
                <div className="text-sm text-white font-medium mb-1">{s.title}</div>
                <p className="text-[11px] text-[#A1A1AA] leading-relaxed">{s.why}</p>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Upcoming + Recent documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card testid="upcoming-tasks">
          <SectionTitle icon={CalendarClock}>Upcoming tasks & deadlines</SectionTitle>
          {d.upcoming.length === 0 ? <p className="text-sm text-[#52525B]">Nothing due right now. Add a task to stay on track.</p> :
            <div className="space-y-2">
              {d.upcoming.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid="upcoming-item">
                  <div className="min-w-0">
                    <div className="text-sm text-[#D4D4D8] truncate">{u.title}</div>
                    {u.due && <div className="text-[11px] text-[#52525B]">Due {u.due}</div>}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA] capitalize shrink-0">{u.type}</span>
                </div>
              ))}
            </div>}
          <button onClick={() => navigate("/progress")} className="text-xs text-[#D4AF37] hover:underline mt-3" data-testid="add-task-btn">+ Add a task</button>
        </Card>

        <Card testid="recent-documents">
          <SectionTitle icon={FileText}>Recent documents</SectionTitle>
          {d.recent_documents.length === 0 ? <p className="text-sm text-[#52525B]">No documents yet. Generated plans, agreements and reports will appear here.</p> :
            <div className="space-y-2">
              {d.recent_documents.map((doc) => (
                <div key={doc.doc_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid="recent-doc-item">
                  <div className="min-w-0">
                    <div className="text-sm text-[#D4D4D8] truncate">{doc.name}</div>
                    <div className="text-[11px] text-[#52525B]">{doc.type} · {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : ""}</div>
                  </div>
                  <button onClick={() => navigate("/documents")} className="text-xs text-[#D4AF37] hover:underline shrink-0">Open</button>
                </div>
              ))}
            </div>}
        </Card>
      </div>

      {/* Achievements + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card testid="achievements">
          <SectionTitle icon={Award}>Achievements</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {d.achievements.map((a) => (
              <div key={a.key} data-testid={`achievement-${a.key}`}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${a.earned ? "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/8 text-[#52525B]"}`}>
                {a.earned ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {a.label}
              </div>
            ))}
          </div>
        </Card>

        <Card testid="recent-activity">
          <SectionTitle icon={ActivityIcon}>Recent activity</SectionTitle>
          {(!d.recent_activity || d.recent_activity.length === 0) ? <p className="text-sm text-[#52525B]">Your actions across the platform will appear here.</p> :
            <div className="space-y-3">
              {d.recent_activity.map((a) => (
                <div key={a.activity_id} className="flex items-center gap-3" data-testid="activity-item">
                  <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><ActivityIcon className="w-4 h-4 text-[#D4AF37]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#D4D4D8] truncate">{a.title}</div>
                    <div className="text-[11px] text-[#52525B]">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>}
        </Card>
      </div>

      {/* Quick actions */}
      <Card testid="quick-actions">
        <SectionTitle icon={Rocket}>Quick actions</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {QUICK.map((q) => {
            const Icon = q.icon;
            return (
              <button key={q.key} onClick={() => navigate(q.path)} data-testid={`quick-${q.key}`}
                className="flex flex-col items-center gap-2 rounded-xl border border-white/8 bg-[#0f0f11] hover:border-[#D4AF37]/40 p-4 transition-colors text-center">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <span className="text-xs text-[#D4D4D8] leading-tight">{q.label}</span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export { PageHeader };
